import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Input, Modal, Popconfirm, Radio, Select, Space, Switch, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, KeyOutlined, PlusOutlined, QuestionCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import PageHeader from '../../../shared/components/PageHeader';
import { CopyBtn, CopyLine, PERMISSIONS, fmtDate, listOf, permLabel, s3, toastErr } from './common';

const { Text } = Typography;

const PERM_TIPS = {
    read: 'List and download objects',
    readwrite: 'Also upload and delete objects',
    admin: 'Also change bucket CORS and lifecycle via S3',
};

const KeyModal = ({ open, record, buckets, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const scope = Form.useWatch('scope', form);

    useEffect(() => {
        if (!open) return;
        form.setFieldsValue(record
            ? { name: record.name, permission: record.permission, scope: record.scope || 'all', bucket_ids: record.bucket_ids || [] }
            : { name: '', permission: 'readwrite', scope: 'all', bucket_ids: [] });
    }, [open, record, form]);

    const submit = async () => {
        const v = await form.validateFields();
        const body = { name: v.name.trim(), permission: v.permission, scope: v.scope, bucket_ids: v.scope === 'buckets' ? (v.bucket_ids || []) : [] };
        setSaving(true);
        try {
            const d = record ? await s3.put(`/keys/${record.id}`, body) : await s3.post('/keys', body);
            onSaved?.(d, !record);
        } catch (e) { toastErr(e); } finally { setSaving(false); }
    };

    return (
        <Modal open={open} title={record ? t('Edit access key') : t('Create access key')} onCancel={onClose} onOk={submit}
            confirmLoading={saving} okText={record ? t('Save') : t('Create')} destroyOnHidden>
            <Form form={form} layout="vertical" requiredMark={false}>
                <Form.Item name="name" label={t('Name')} rules={[{ required: true, whitespace: true, message: t('Enter a name') }]}>
                    <Input maxLength={64} placeholder="backup-server" autoFocus />
                </Form.Item>
                <Form.Item name="permission" label={t('Permission')}>
                    <Radio.Group optionType="button" options={PERMISSIONS.map((p) => ({
                        value: p.value,
                        label: <Tooltip title={t(PERM_TIPS[p.value])}>{t(p.label)}</Tooltip>,
                    }))} />
                </Form.Item>
                <Form.Item name="scope" label={t('Buckets')}>
                    <Radio.Group options={[{ value: 'all', label: t('All buckets') }, { value: 'buckets', label: t('Selected buckets') }]} />
                </Form.Item>
                {scope === 'buckets' && (
                    <Form.Item name="bucket_ids" rules={[{ required: true, type: 'array', min: 1, message: t('Select at least one bucket') }]}>
                        <Select mode="multiple" placeholder={t('Select buckets')} optionFilterProp="label"
                            options={buckets.map((b) => ({ value: b.id, label: `${b.name} (${b.region_name || b.region || ''})` }))} />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

const snippets = (k, region) => {
    const ep = region?.endpoint || 'https://slc1.s3.yeapcloud.com';
    const rc = region?.code || 'slc1';
    const id = k.access_key_id;
    const sec = k.secret_access_key;
    return {
        cli: [
            `aws configure set aws_access_key_id ${id} --profile yeap`,
            `aws configure set aws_secret_access_key ${sec} --profile yeap`,
            `aws configure set region ${rc} --profile yeap`,
            '',
            `aws s3 ls --profile yeap --endpoint-url ${ep}`,
        ].join('\n'),
        boto3: [
            'import boto3',
            '',
            's3 = boto3.client(',
            "    's3',",
            `    endpoint_url='${ep}',`,
            `    region_name='${rc}',`,
            `    aws_access_key_id='${id}',`,
            `    aws_secret_access_key='${sec}',`,
            ')',
            'print(s3.list_buckets())',
        ].join('\n'),
        js: [
            "import { S3Client, ListBucketsCommand } from '@aws-sdk/client-s3';",
            '',
            'const s3 = new S3Client({',
            `  endpoint: '${ep}',`,
            `  region: '${rc}',`,
            `  credentials: { accessKeyId: '${id}', secretAccessKey: '${sec}' },`,
            '});',
            'console.log(await s3.send(new ListBucketsCommand({})));',
        ].join('\n'),
    };
};

const SecretModal = ({ secret, regions, onClose }) => {
    const { t } = useTranslation();
    const [regionId, setRegionId] = useState(null);
    useEffect(() => { if (secret) setRegionId(regions?.[0]?.id ?? null); }, [secret, regions]);
    if (!secret) return null;
    const region = (regions || []).find((r) => r.id === regionId) || regions?.[0];
    const sn = snippets(secret, region);
    const code = (text) => (
        <div style={{ position: 'relative' }}>
            <pre style={{ margin: 0, padding: 12, paddingRight: 40, borderRadius: 6, background: 'var(--ant-color-fill-tertiary, #f5f5f5)', fontSize: 12, overflow: 'auto' }}>{text}</pre>
            <div style={{ position: 'absolute', top: 6, right: 6 }}><CopyBtn text={text} /></div>
        </div>
    );
    return (
        <Modal open title={t('Access key created')} width={680} closable={false} maskClosable={false} keyboard={false}
            footer={<Button type="primary" onClick={onClose}>{t("I've saved it")}</Button>}>
            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t('The secret is shown only once')} />
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <Text type="secondary">{t('Access key ID')}</Text>
                <CopyLine value={secret.access_key_id} />
                <Text type="secondary">{t('Secret access key')}</Text>
                <CopyLine value={secret.secret_access_key} />
            </Space>
            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text type="secondary">{t('Region')}</Text>
                <Select size="small" style={{ minWidth: 200 }} value={region?.id}
                    onChange={setRegionId} options={(regions || []).map((r) => ({ value: r.id, label: `${r.name} (${r.code})` }))} />
            </div>
            <Tabs size="small" items={[
                { key: 'cli', label: 'AWS CLI', children: code(sn.cli) },
                { key: 'py', label: 'Python (boto3)', children: code(sn.boto3) },
                { key: 'js', label: 'JavaScript', children: code(sn.js) },
            ]} />
        </Modal>
    );
};

const S3Keys = () => {
    const { t } = useTranslation();
    const [keys, setKeys] = useState([]);
    const [buckets, setBuckets] = useState([]);
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ open: false, record: null });
    const [secret, setSecret] = useState(null);
    const [toggling, setToggling] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [k, b, o] = await Promise.all([
                s3.get('/keys'),
                s3.get('/buckets').catch(() => []),
                s3.get('/overview').catch(() => null),
            ]);
            setKeys(listOf(k));
            setBuckets(listOf(b));
            setRegions(Array.isArray(o?.regions) ? o.regions : []);
        } catch (e) { toastErr(e, t('Failed to load')); } finally { setLoading(false); }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const bucketName = useMemo(() => Object.fromEntries(buckets.map((b) => [b.id, b.name])), [buckets]);

    const toggle = async (k, enabled) => {
        setToggling(k.id);
        try {
            await s3.put(`/keys/${k.id}/enabled`, { enabled });
            setKeys((x) => x.map((y) => (y.id === k.id ? { ...y, status: enabled ? 'active' : 'disabled' } : y)));
        } catch (e) { toastErr(e); } finally { setToggling(null); }
    };

    const remove = async (k) => {
        try {
            await s3.del(`/keys/${k.id}`);
            setKeys((x) => x.filter((y) => y.id !== k.id));
        } catch (e) { toastErr(e); }
    };

    const cols = [
        { title: t('Name'), dataIndex: 'name' },
        { title: t('Access key ID'), dataIndex: 'access_key_id', render: (v) => <CopyLine value={v} /> },
        {
            title: t('Permission'), dataIndex: 'permission', width: 130,
            render: (v) => <Tag color={v === 'admin' ? 'red' : v === 'readwrite' ? 'blue' : 'default'} style={{ margin: 0 }}>{permLabel(v)}</Tag>,
        },
        {
            title: t('Buckets'), key: 'scope', width: 200,
            render: (_, k) => (k.scope === 'buckets'
                ? <Space size={[4, 4]} wrap>{(k.bucket_ids || []).map((id) => <Tag key={id} style={{ margin: 0 }}>{bucketName[id] || `#${id}`}</Tag>)}</Space>
                : <Text type="secondary">{t('All buckets')}</Text>),
        },
        { title: t('Last used'), dataIndex: 'last_used_at', width: 170, responsive: ['lg'], render: (v) => (v ? fmtDate(v) : <Text type="secondary">{t('Never')}</Text>) },
        { title: t('Created'), dataIndex: 'created_at', width: 170, responsive: ['xl'], render: fmtDate },
        {
            title: t('Enabled'), dataIndex: 'status', width: 90,
            render: (v, k) => <Switch size="small" checked={v === 'active'} loading={toggling === k.id} onChange={(c) => toggle(k, c)} />,
        },
        {
            title: '', key: 'a', width: 90, align: 'right',
            render: (_, k) => (
                <Space size={0}>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setModal({ open: true, record: k })} />
                    <Popconfirm title={t('Delete this key? Apps using it will stop working.')} okText={t('Delete')} okButtonProps={{ danger: true }}
                        onConfirm={() => remove(k)}>
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <PageHeader title={<span>{t('Access keys')}
                <Tooltip title={t('One key works in every region')}><QuestionCircleOutlined style={{ fontSize: 14, marginLeft: 8, color: 'var(--ant-color-text-tertiary, #999)' }} /></Tooltip>
            </span>} icon={<KeyOutlined />}
                extra={<Space>
                    <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal({ open: true, record: null })}>{t('Create access key')}</Button>
                </Space>} />
            <Card styles={{ body: { padding: 0 } }}>
                <Table rowKey="id" columns={cols} dataSource={keys} loading={loading} size="middle" scroll={{ x: 900 }}
                    pagination={keys.length > 20 ? { pageSize: 20 } : false}
                    locale={{ emptyText: t('No access keys yet') }} />
            </Card>

            <KeyModal open={modal.open} record={modal.record} buckets={buckets}
                onClose={() => setModal({ open: false, record: null })}
                onSaved={(d, created) => {
                    setModal({ open: false, record: null });
                    if (created && d?.secret_access_key) setSecret(d);
                    load();
                }} />
            <SecretModal secret={secret} regions={regions} onClose={() => setSecret(null)} />
        </div>
    );
};

export default S3Keys;
